import React from 'react';
import Layout from '../components/Layout';
import PageHero from '../components/PageHero';
import ContactCTA from '../components/ContactCTA';
import AISection from '../components/AISection';
import SEO from '../components/SEO';
import { combineSchemas, organizationSchema, serviceSchema, breadcrumbSchema } from '../lib/schema';
import { Bot, Workflow, MessageSquare, BarChart3, Sparkles, Cpu } from 'lucide-react';

const services = [
  { icon: Bot, title: 'AI Chatbots', desc: 'Conversational assistants trained on your business that answer questions, qualify leads and book appointments 24/7.' },
  { icon: Workflow, title: 'Workflow Automation', desc: 'Connect your CRM, email, scheduling and inventory tools so repetitive tasks run themselves.' },
  { icon: MessageSquare, title: 'AI Content & Copy', desc: 'On-brand product descriptions, blog posts and email campaigns generated and optimized for SEO.' },
  { icon: BarChart3, title: 'Predictive Analytics', desc: 'Forecast demand, identify high-value customers and surface insights hidden in your data.' },
  { icon: Sparkles, title: 'AI Personalization', desc: 'Show every visitor the right offer, page or product based on real-time behavior.' },
  { icon: Cpu, title: 'Custom AI Integrations', desc: 'OpenAI, Anthropic and open-source models wired directly into your existing stack.' },
];

const AIPage = () => (
  <Layout>
    <SEO
      title="A.I. Integration Services for Businesses | Website Symphony"
      description="Custom A.I. chatbots, workflow automation, predictive analytics and AI content generation for your business. Practical A.I. that pays for itself. Free consultation."
      keywords="AI integration, AI chatbot for business, AI website integration, workflow automation, predictive analytics, AI marketing, custom AI solutions"
      canonical="/ai"
      schema={combineSchemas(
        organizationSchema,
        serviceSchema('A.I. Integration Services', 'Custom AI chatbots, workflow automation, predictive analytics and AI content generation for businesses.', '/ai'),
        breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'A.I.', path: '/ai' }]),
      )}
    />
    <PageHero
      title="Artificial Intelligence Services"
      subtitle="Smart automation, conversational AI and data intelligence — integrated into your website and workflows."
      breadcrumb="A.I."
    />

    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <h2 className="text-[#2C5F66] text-3xl md:text-4xl font-extrabold mb-4">Practical A.I. that drives real results</h2>
          <p className="text-gray-600 max-w-3xl mx-auto text-[15px] leading-relaxed">
            We focus on AI that pays for itself — chatbots that capture leads while you sleep, automations that save hours every week, and analytics that show you exactly where to grow next.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => (
            <div key={s.title} className="group bg-[#F7F7F5] rounded-lg p-7 hover:bg-[#2C5F66] hover:text-white transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 rounded-lg bg-[#2C5F66]/10 group-hover:bg-white/10 flex items-center justify-center mb-5 transition-colors">
                <s.icon className="text-[#2C5F66] group-hover:text-[#D9A04A]" size={26} />
              </div>
              <h3 className="text-[#2C5F66] group-hover:text-white text-lg font-bold mb-2">{s.title}</h3>
              <p className="text-gray-600 group-hover:text-white/80 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <AISection />
    <ContactCTA defaultService="A.I. Integration" heading="Let A.I. work for your business" />
  </Layout>
);

export default AIPage;
